// FreshnessIndicator — ekrandaki rakamların ne zamana ait olduğunu söyleyen
// kompakt gösterge. Mobile-first: 3 chunk (NAV / Endeks / RSI) flex-wrap ile
// satıra sığar; sığmayanlar 2. satıra düşer. Stale ise ⚠️ rozet.
//
// Üç farklı veri kaynağının taze tarihi gösterilir:
//   - NAV (TEFAS T+1) — `lastPriceDate`
//   - KFE / TÜFE (TCMB EVDS, aylık) — `indexPeriod`
//   - RSI (sinyal motoru, günlük 19:00 TR) — `signalRefreshedAt`
//
// Eşikler (stale tanımı):
//   - NAV > 2 iş günü eski
//   - sinyal > 24 saat eski
//   - endeks > 2 ay eski
//
// 2026-05-28 iPhone 12 (390px) layout fix: önceki sürüm `inline-flex` ile
// her label içindeki text mobile container'da satır içinde sarıyordu
// ("son güncelleme:" → "son" / "güncelleme:"). Yeni sürüm: `flex-wrap` +
// her chunk `whitespace-nowrap` + kısa tarih format.

interface Props {
  /** ISO tarih (YYYY-MM-DD veya tam ISO) — TEFAS scrape sonrası last_price_date */
  lastPriceDate?: string | null;
  /** KFE/TÜFE periyodu YYYY-MM */
  indexPeriod?: string;
  /** Sinyal motoru son tarama ISO timestamp */
  signalRefreshedAt?: string;
  /** Mock işareti (gerçek API bağlanmadıysa belirgin yap) */
  mock?: boolean;
  /** Kompakt mode — sinyal detayında daha küçük */
  compact?: boolean;
}

function formatLastPriceShort(input?: string | null): string {
  if (!input) return "—";
  // "26.05 19:00" gibi kısa form (yıl atlanır — gün/ay yeter).
  const d = new Date(input.length === 10 ? input + "T19:00:00+03:00" : input);
  if (Number.isNaN(d.getTime())) return input;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm} ${hh}:${mi}`;
}

function formatSignalTime(input?: string): string {
  if (!input) return "—";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return input;
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
}

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? iso + "T00:00:00+03:00" : iso);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
}

export default function FreshnessIndicator({
  lastPriceDate = "2026-05-26",
  indexPeriod = "2026-04",
  signalRefreshedAt = "2026-05-26T19:00:00+03:00",
  mock = true,
  compact = false,
}: Props) {
  const navAgeDays = daysSince(lastPriceDate);
  const signalAgeHours =
    signalRefreshedAt && !Number.isNaN(new Date(signalRefreshedAt).getTime())
      ? (Date.now() - new Date(signalRefreshedAt).getTime()) / (1000 * 60 * 60)
      : null;
  const indexAgeDays = indexPeriod
    ? daysSince(`${indexPeriod}-01`)
    : null;

  const navStale = navAgeDays !== null && navAgeDays > 2;
  const signalStale = signalAgeHours !== null && signalAgeHours > 24;
  const indexStale = indexAgeDays !== null && indexAgeDays > 62;

  const stale = navStale || signalStale || indexStale;

  const tooltipMsg = stale
    ? [
        navStale ? "NAV 2 iş gününden eski (TEFAS scrape gecikti)" : null,
        signalStale
          ? "Sinyal taraması 24 saatten eski (sinyal motoru cron gecikti)"
          : null,
        indexStale ? "KFE/TÜFE 2 aydan eski (TCMB EVDS güncellenmedi)" : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Veriler güncel";

  const sizeText = compact ? "text-[10px]" : "text-[11px]";
  const containerPad = compact ? "px-2 py-1" : "px-3 py-1.5";

  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border ${
        stale
          ? "border-warn-500/40 bg-warn-500/5"
          : "border-ink-700/60 bg-ink-800/40"
      } ${containerPad} ${sizeText} text-ink-300 leading-tight`}
      role="status"
      aria-live="polite"
      title={tooltipMsg}
    >
      {mock && (
        <span
          className="uppercase tracking-wider text-[9px] text-warn-400 font-semibold whitespace-nowrap"
          aria-label="Mock veri"
        >
          MOCK
        </span>
      )}
      {stale && (
        <span aria-label="Uyarı" title={tooltipMsg} className="whitespace-nowrap">
          ⚠️
        </span>
      )}
      <span className="whitespace-nowrap inline-flex items-baseline gap-1">
        <span className="text-ink-400">NAV</span>
        <span className="fon-dili text-ink-200">
          {formatLastPriceShort(lastPriceDate)}
        </span>
      </span>
      <span className="whitespace-nowrap inline-flex items-baseline gap-1">
        <span className="text-ink-400">Endeks</span>
        <span className="fon-dili text-ink-200">{indexPeriod}</span>
      </span>
      {!compact && (
        <span className="whitespace-nowrap inline-flex items-baseline gap-1">
          <span className="text-ink-400">RSI</span>
          <span className="fon-dili text-ink-200">
            {formatSignalTime(signalRefreshedAt)}
          </span>
        </span>
      )}
    </div>
  );
}
